import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

/*
 * Ein Zähler, der Bildschirmwechsel herunterzählt.
 *
 * Beide Ausnahmen am Hilfe-Knopf gelten auf Zeit und keine davon für immer: Er
 * lässt sich **wegklicken** und er lässt sich **verschieben** — und nach fünf
 * Bildschirmwechseln steht er wieder unten rechts, dort, wo ihn alle suchen.
 *
 * Warum auf Zeit: „Für immer" wäre entweder eine Einstellung, die niemand
 * wiederfindet, oder ein Weg, den Kanal versehentlich dauerhaft zu schließen
 * beziehungsweise den Knopf dauerhaft an eine Stelle zu legen, an der er später
 * stört. Wer ihn gerade im Weg hat, wird ihn los; wer ihn morgen braucht,
 * findet ihn an der gewohnten Stelle.
 *
 * Gezählt wird der Wechsel des **Seitenpfads**, nicht jeder Reiter innerhalb
 * einer Seite: Sonst wäre der Knopf nach fünf Klicks im selben Fenster wieder
 * da, mitten in der Arbeit, wegen der man ihn weggeklickt hat.
 *
 * Der Zähler lebt in der Sitzung (`sessionStorage`), nicht dauerhaft: Nach dem
 * nächsten Anmelden ist der Ausgangszustand hergestellt. Ein Neuladen der Seite
 * soll die Entscheidung dagegen nicht aufheben — sonst umginge F5 sie.
 */

export const WECHSEL_BIS_ZURUECK = 5

const lies = key => {
	try {
		const roh = window.sessionStorage.getItem(key)
		if (!roh) return null
		const wert = JSON.parse(roh)
		return Number.isFinite(wert?.rest) && wert.rest > 0 ? wert : null
	} catch (error) {
		return null
	}
}

const schreibe = (key, wert) => {
	try {
		if (wert) window.sessionStorage.setItem(key, JSON.stringify(wert))
		else window.sessionStorage.removeItem(key)
	} catch (error) {
		// Ohne Speicher gilt die Entscheidung nur für diese Seite. Kein Grund,
		// den Knopf gar nicht erst zu zeigen.
	}
}

/**
 * @param {string} key   Ablage in der Sitzung.
 * @param {number} [wechsel] Bildschirmwechsel, die die Ausnahme überdauert.
 * @returns {{wert: object|null, starte: Function, passeAn: Function, beende: Function}}
 *   `wert` ist `null`, solange keine Ausnahme läuft, sonst die mitgegebenen
 *   Angaben (bei der Lage die Koordinaten) samt Restzähler.
 */
export default function useNavigationCountdown(key, wechsel = WECHSEL_BIS_ZURUECK) {
	const location = useLocation()
	const [wert, setWert] = useState(() => lies(key))
	const letzterPfad = useRef(location.pathname)

	useEffect(() => {
		if (location.pathname === letzterPfad.current) return
		letzterPfad.current = location.pathname

		setWert(prev => {
			if (!prev) return prev
			const rest = prev.rest - 1
			const naechst = rest > 0 ? { ...prev, rest } : null
			schreibe(key, naechst)
			return naechst
		})
	}, [key, location.pathname])

	/** Ausnahme beginnen — oder von vorn beginnen, wenn schon eine läuft. */
	const starte = useCallback(
		(nutzlast = {}) => {
			const neu = { ...nutzlast, rest: wechsel }
			schreibe(key, neu)
			setWert(neu)
		},
		[key, wechsel]
	)

	/**
	 * Angaben ändern, ohne den Zähler neu zu stellen — für das Nachziehen der
	 * Lage beim Verkleinern des Fensters.
	 */
	const passeAn = useCallback(
		aendere => {
			setWert(prev => {
				if (!prev) return prev
				const naechst = { ...prev, ...aendere(prev) }
				schreibe(key, naechst)
				return naechst
			})
		},
		[key]
	)

	const beende = useCallback(() => {
		schreibe(key, null)
		setWert(null)
	}, [key])

	return { wert, starte, passeAn, beende }
}
